import { RefObject, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';

import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import {
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

import { DonutTier } from '../../tier';
import { DonutTooltip } from './parts/DonutTooltip';
import { formatPercent, formatValue } from './helpers';
import { DonutLayout, layoutForTier, shouldRenderHtmlLegend } from './layout';
import { ChartDatum, DonutUnit } from './types';

echarts.use([
  PieChart,
  TooltipComponent,
  LegendComponent,
  LabelLayout,
  CanvasRenderer,
]);

type RenderState = {
  data: ChartDatum[];
  colors: string[];
  unit: DonutUnit;
  hidden: Set<string>;
  tier: DonutTier;
};

interface UseGenerateDonutChartParams {
  containerRef: RefObject<HTMLDivElement>;
  onLegendChange: (selected: Record<string, boolean>) => void;
}

interface UseGenerateDonutChartReturn {
  renderChart: (state: RenderState) => void;
  toggleLegendItem: (name: string) => void;
}

interface TooltipParams {
  name: string;
  value: number;
  data: ChartDatum;
  color: string;
}

function buildTooltipFormatter(unit: DonutUnit): (params: TooltipParams) => string {
  return (params) => ReactDOMServer.renderToString(
    <DonutTooltip
      color={params.color}
      name={params.name}
      formattedValue={formatValue(params.value, unit)}
      formattedPercentage={formatPercent(params.data.percentage)}
    />,
  );
}

function buildLegendOption(layout: DonutLayout, selected: Record<string, boolean>, stacked: boolean): echarts.EChartsCoreOption['legend'] {
  if (stacked) return { show: false, selected };
  return {
    type: 'scroll',
    orient: 'vertical',
    right: '4%',
    top: 'middle',
    icon: 'rect',
    itemWidth: layout.legendItemSize,
    itemHeight: layout.legendItemSize,
    itemGap: 10,
    textStyle: { fontSize: layout.legendFontSize, color: '#161922' },
    selected,
  };
}

function buildOption(state: RenderState, containerWidth: number): echarts.EChartsCoreOption {
  const layout = layoutForTier(state.tier);
  const stacked = shouldRenderHtmlLegend(state.tier);
  const outerPx = Math.max(
    layout.outerMin,
    Math.min(containerWidth * layout.outerScale, layout.outerMax),
  );
  const innerPx = outerPx * layout.innerRatio;
  const selected = Object.fromEntries(
    state.data.map((item) => [item.name, !state.hidden.has(item.name)]),
  );

  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      borderWidth: 0,
      borderColor: 'transparent',
      formatter: buildTooltipFormatter(state.unit),
    },
    legend: buildLegendOption(layout, selected, stacked),
    color: state.colors,
    series: [
      {
        type: 'pie',
        radius: [innerPx, outerPx],
        center: [layout.centerX, layout.centerY],
        avoidLabelOverlap: true,
        emphasis: {
          scale: false,
          scaleSize: 0,
          itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent',
            shadowBlur: 0,
          },
        },
        label: {
          show: true,
          formatter: (p: { data: ChartDatum }) => `${formatPercent(p.data.percentage)}%`,
          fontSize: layout.labelFontSize,
          color: '#161922',
        },
        labelLine: {
          show: true,
          length: layout.labelLength,
          length2: layout.labelLength2,
        },
        data: state.data,
      },
    ],
  };
}

function applyOption(
  chart: echarts.ECharts,
  state: RenderState,
  prevStacked: boolean | null,
): boolean {
  const node = chart.getDom() as HTMLDivElement;
  const containerWidth = node.clientWidth || 320;
  const stacked = shouldRenderHtmlLegend(state.tier);
  const layoutChanged = prevStacked !== stacked;
  chart.dispatchAction({ type: 'hideTip' });
  chart.setOption(
    buildOption(state, containerWidth),
    layoutChanged ? { replaceMerge: ['legend'] } : {},
  );
  return stacked;
}

export function useGenerateDonutChart({
  containerRef,
  onLegendChange,
}: UseGenerateDonutChartParams): UseGenerateDonutChartReturn {
  const chartRef = useRef<echarts.ECharts | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const stateRef = useRef<RenderState | null>(null);
  const prevStackedRef = useRef<boolean | null>(null);

  function ensureChart(node: HTMLDivElement): echarts.ECharts {
    if (chartRef.current) {
      if (chartRef.current.getDom() === node) return chartRef.current;
      observerRef.current?.disconnect();
      observerRef.current = null;
      chartRef.current.dispose();
      chartRef.current = null;
      prevStackedRef.current = null;
    }
    const chart = echarts.init(node);
    chartRef.current = chart;
    chart.setOption({
      animation: true,
      animationDuration: 800,
      animationDurationUpdate: 1000,
      animationEasing: 'cubicOut',
      animationEasingUpdate: 'cubicInOut',
    });
    chart.on('legendselectchanged', (...args: unknown[]) => {
      const params = args[0] as { selected?: Record<string, boolean> } | undefined;
      if (params?.selected) onLegendChange(params.selected);
    });
    if (typeof ResizeObserver !== 'undefined') {
      let pending = 0;
      observerRef.current = new ResizeObserver(() => {
        if (pending) return;
        pending = window.requestAnimationFrame(() => {
          pending = 0;
          const current = chartRef.current;
          const state = stateRef.current;
          if (!current || !state) return;
          current.resize();
          prevStackedRef.current = applyOption(current, state, prevStackedRef.current);
        });
      });
      observerRef.current.observe(node);
    }
    return chart;
  }

  function renderChart(state: RenderState): void {
    const node = containerRef.current;
    if (!node) return;
    stateRef.current = state;
    const chart = ensureChart(node);
    prevStackedRef.current = applyOption(chart, state, prevStackedRef.current);
  }

  function toggleLegendItem(name: string): void {
    chartRef.current?.dispatchAction({ type: 'legendToggleSelect', name });
  }

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return { renderChart, toggleLegendItem };
}
